import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';
import { Plus, Pencil, Trash2, Megaphone, RefreshCw } from 'lucide-react';

const AdminAnnouncements = () => {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'update',
    image_url: ''
  });

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/announcements/${editing}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Announcement updated!');
      } else {
        await axios.post(`${API}/announcements`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Announcement created!');
      }
      setShowDialog(false);
      setFormData({ title: '', content: '', category: 'update', image_url: '' });
      setEditing(null);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      content: item.content,
      category: item.category,
      image_url: item.image_url || ''
    });
    setEditing(item.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await axios.delete(`${API}/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement deleted!');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const categoryColors = {
    truth_tracker: 'bg-emerald-500',
    update: 'bg-blue-500',
    event: 'bg-purple-500'
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-plp-blue" />
            Announcements
          </h1>
          <p className="text-slate-600">Manage Truth Tracker and campaign updates</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditing(null);
            setFormData({ title: '', content: '', category: 'update', image_url: '' });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="add-announcement-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Announcement' : 'New Announcement'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truth_tracker">Truth Tracker</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                  placeholder="Announcement title"
                  data-testid="announcement-title-input"
                />
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData(p => ({ ...p, content: e.target.value }))}
                  placeholder="Announcement content..."
                  rows={4}
                  data-testid="announcement-content-input"
                />
              </div>
              <ImageUploader
                label="Image (optional)"
                value={formData.image_url}
                onChange={(url) => setFormData(p => ({ ...p, image_url: url }))}
                placeholder="https://example.com/image.jpg"
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-plp-blue" data-testid="save-announcement-btn">
                  {editing ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-plp-blue" />
        </div>
      ) : announcements.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No announcements yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((item) => (
            <Card key={item.id} className="bg-white" data-testid={`announcement-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Badge className={`${categoryColors[item.category]} text-white mb-2`}>
                      {item.category.replace('_', ' ')}
                    </Badge>
                    <h3 className="font-heading font-bold text-lg">{item.title}</h3>
                    <p className="text-slate-600 mt-1 line-clamp-2">{item.content}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(item.created_at).toLocaleDateString()} | 
                      Helpful: {item.helpful_yes} Yes / {item.helpful_no} No
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncements;
